import { useEffect, useRef } from "react";
import { useFeatureFlags } from "@/lib/backend/utils/hooks/use-feature-flags";
import { useUpdateAppVersion } from "@/lib/query/me/use-app-version";
import { usePreferencesStore } from "@/lib/store/preferences";

/**
 * Til een oude, lokale "zet me terug op de vorige app" één keer naar de server.
 *
 * De keuze woonde eerder in de opslag van het toestel. Wie hem daar ooit omzette heeft dat gemeend,
 * en zonder deze stap zou de eerste keer dat hij de bijgewerkte app opent hem terugzetten op de
 * nieuwe versie -- precies wat hij toen wegklikte.
 *
 * Eenrichtingsverkeer, en alleen als er nog niets op de server staat: `false` is wat er lokaal
 * bewaard kon worden ("ik wil de oude"), en `null` op de server betekent "nog niets gekozen". Wie
 * op de server al iets heeft, heeft dat op een nieuwer moment gekozen dan wat er op dit toestel
 * lag.
 *
 * Daarna wordt de lokale vlag gewist, zodat dit precies één keer gebeurt en een tweede toestel niet
 * later alsnog een keuze van jaren geleden komt opdringen.
 */
export function useAppReworkMigration() {
  const { data, isFetched } = useFeatureFlags();
  const optedOut = usePreferencesStore((state) => state.appReworkOptOut);
  const setAppReworkOptOut = usePreferencesStore((state) => state.setAppReworkOptOut);
  const updateAppVersion = useUpdateAppVersion();
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current || !isFetched || !optedOut) {
      return;
    }

    // Niets te verhuizen naar een organisatie die geen keuze kent, en niets te overschrijven als de
    // server er al een heeft.
    if (!data.appReworkSwitchable || data.appReworkChoice !== null) {
      return;
    }

    sent.current = true;
    updateAppVersion.mutate(false, {
      // De lokale vlag pas wissen als de server hem heeft: gaat de aanroep mis, dan blijft hij
      // staan en probeert de app het de volgende keer opnieuw.
      onSuccess: () => setAppReworkOptOut(false),
      onError: () => {
        sent.current = false;
      },
    });
  }, [isFetched, optedOut, data, updateAppVersion, setAppReworkOptOut]);
}
